<template>
  <div>
    <div class="page-header">
      <div class="header-left">
        <button class="btn btn-secondary back-button" @click="goBack">
          <i class="fa fa-arrow-left"></i> Back
        </button>
        <h4 class="table-page-header admin-name">{{ adminName }}</h4>

        <!-- Tab Buttons in Header -->
        <div class="button-toggle-row">
          <el-button
            :type="activeTab === 'details' ? 'primary' : 'default'"
            @click="activeTab = 'details'"
            class="toggle-button"
          >
            Details
          </el-button>
          <el-button
            :type="activeTab === 'training' ? 'primary' : 'default'"
            @click="activeTab = 'training'; loadTrainingTabData();"
            class="toggle-button"
          >
            Training
          </el-button>
          <el-button
            :type="activeTab === 'compliance' ? 'primary' : 'default'"
            @click="activeTab = 'compliance'; loadComplianceProfile();"
            class="toggle-button"
          >
            Compliance
          </el-button>
          <el-button
            :type="activeTab === 'client-access' ? 'primary' : 'default'"
            @click="activeTab = 'client-access'; loadClientAccess();"
            class="toggle-button"
          >
            Client Access
          </el-button>
          <el-button
            :type="activeTab === 'calendar' ? 'primary' : 'default'"
            @click="activeTab = 'calendar'"
            class="toggle-button"
          >
            Calendar
          </el-button>
        </div>
      </div>
    </div>

    <!-- Tab Content -->
    <div class="admin-tab-content">

      <!-- Details Tab -->
      <div v-if="activeTab === 'details'" class="admin-tab-pane">
        <div v-if="loadingAdmin" class="text-center p-4">
          <i class="fa fa-spinner fa-spin"></i> Loading admin details...
        </div>
        <div v-else-if="admin" class="admin-details-content">
          <el-form label-position="top" label-width="120px" class="full-width-form">
            <AIVRow
              :record.sync="admin"
              :fields="[
                { label: 'First Name', key: 'first_name' },
                { label: 'Last Name', key: 'last_name' },
                { label: 'Email', key: 'email' },
              ]"
            />
            <AIVRow
              :record.sync="admin.address"
              :fields="[
                { label: 'Address1', key: 'address1' },
                { label: 'Address2', key: 'address2' },
              ]"
            />
            <AIVRow
              :record.sync="admin.address"
              :fields="[
                { label: 'City', key: 'city' },
                { label: 'State', key: 'state' },
                { label: 'Zip', key: 'zip' },
              ]"
            />
            <AIVRow
              :record.sync="admin"
              :fields="[
                { label: 'Mobile Phone', key: 'mobile_phone' },
                { label: 'Other Phone', key: 'other_phone' },
              ]"
            />
            <AIVRow
              :record.sync="admin"
              :fields="[
                { label: 'Hire Date', key: 'hire_date', type: 'date' },
              ]"
            />
            <AIVRow
              :record.sync="admin"
              :fields="[
                { label: 'Title', key: 'title' },
                {
                  label: 'Business Unit',
                  key: 'business_unit',
                  type: 'select',
                  options: businessUnitOptions,
                  placeholder: 'Select Business Unit',
                },
                { label: 'Position ID', key: 'position_id' },
              ]"
            />
            <AIVRow
              :record.sync="admin"
              :fields="[
                {
                  label: 'Managed Offices',
                  key: 'managed_offices',
                  type: 'multiselect',
                  options: officeOptions,
                  placeholder: 'Select offices this admin manages',
                },
              ]"
            />
            <div class="text-center mt-4">
              <el-button type="primary" round @click="saveAdminDetails">
                Save Changes
              </el-button>
            </div>
          </el-form>
        </div>
        <div v-else class="text-center p-4">
          <p>No admin details available</p>
        </div>
      </div>

      <!-- Training Tab -->
      <div v-if="activeTab === 'training'" class="admin-tab-pane">
        <div class="training-content">
          <h5>Client Training Status</h5>
          <p class="text-muted mb-3">
            View and manage training records for this admin across all clients.
          </p>

          <div v-if="loadingTraining" class="text-center p-4">
            <i class="fa fa-spinner fa-spin"></i> Loading training records...
          </div>

          <div v-else-if="trainingRecords.length === 0" class="text-center p-4">
            <p>No training records found for this admin.</p>
          </div>

          <div v-else class="training-records">
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Client Name</th>
                    <th>Status</th>
                    <th>Submitted Date</th>
                    <th>Reviewed By</th>
                    <th>Reviewed Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in trainingRecords" :key="record.id">
                    <td>
                      <strong>{{ record.client?.first_name }} {{ record.client?.last_name }}</strong>
                    </td>
                    <td>
                      <span class="badge" :class="{
                        'badge-warning': record.status === 'pending',
                        'badge-success': record.status === 'approved',
                        'badge-danger': record.status === 'rejected',
                      }">
                        {{ record.status.charAt(0).toUpperCase() + record.status.slice(1) }}
                      </span>
                    </td>
                    <td>{{ formatDate(record.submitted_at) }}</td>
                    <td>
                      <span v-if="record.approver">{{ record.approver.first_name }} {{ record.approver.last_name }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span v-if="record.approved_at">{{ formatDate(record.approved_at) }}</span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <button v-if="record.status === 'pending'" class="btn btn-sm btn-success mr-1" @click="approveTraining(record)" title="Approve">
                        <i class="fa fa-check"></i>
                      </button>
                      <button v-if="record.status === 'pending'" class="btn btn-sm btn-danger" @click="rejectTraining(record)" title="Reject">
                        <i class="fa fa-times"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Training Documents & Links -->
        <div class="training-documents-section">
          <h5>Training Documents & Links</h5>

          <div class="documents-upload-section mb-4">
            <div class="row">
              <div class="col-md-4">
                <label>Display Name</label>
                <el-input v-model="newDocument.displayName" placeholder="Enter display name" />
              </div>
              <div class="col-md-4">
                <label>Type</label>
                <AIVSelect v-model="newDocument.type" :options="documentTypeOptions" placeholder="Select type" />
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-md-8">
                <label v-if="newDocument.type === 'file'">Select File</label>
                <label v-else>URL</label>
                <el-input v-if="newDocument.type === 'link'" v-model="newDocument.url" placeholder="Enter URL" />
                <input v-else type="file" ref="trainingFileInput" @change="onTrainingFileChange" class="form-control" />
              </div>
              <div class="col-md-4 d-flex align-items-end">
                <el-button type="primary" @click="uploadTrainingDocument" :loading="uploadingDocument" :disabled="!canUpload" class="upload-document-button">
                  {{ newDocument.type === "file" ? "Upload" : "Add Link" }}
                </el-button>
              </div>
            </div>
          </div>

          <div v-if="loadingDocuments" class="text-center p-3">
            <i class="fa fa-spinner fa-spin"></i> Loading documents...
          </div>

          <div v-else-if="trainingDocuments.length === 0" class="text-center p-3">
            <p class="text-muted">No training documents or links added yet.</p>
          </div>

          <div v-else class="documents-list">
            <div v-for="doc in trainingDocuments" :key="doc.id" class="document-item">
              <div class="document-icon">
                <i :class="doc.is_link ? 'fa fa-link' : 'fa fa-file'"></i>
              </div>
              <div class="document-info">
                <a href="#" class="document-name" @click.prevent="viewTrainingDocument(doc)">
                  {{ doc.display_name || doc.link }}
                </a>
                <div class="document-meta">
                  <span v-if="doc.uploader">{{ doc.uploader.first_name }} {{ doc.uploader.last_name }}</span>
                  <span v-if="doc.createdAt"> - {{ formatDate(doc.createdAt) }}</span>
                </div>
              </div>
              <button class="btn btn-sm btn-danger document-delete" @click="deleteTrainingDocument(doc)" title="Delete">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Manual Training Entry -->
        <div class="manual-training-section mb-4">
          <div class="d-flex align-items-center mb-2">
            <h5 class="mb-0">Add Training Hours</h5>
            <button class="btn btn-sm btn-outline-primary ml-2" @click="showManualTrainingForm = !showManualTrainingForm">
              <i :class="showManualTrainingForm ? 'fa fa-minus' : 'fa fa-plus'"></i>
              {{ showManualTrainingForm ? 'Cancel' : 'New Entry' }}
            </button>
          </div>

          <div v-if="showManualTrainingForm" class="card p-3">
            <div class="row">
              <div class="col-md-4 mb-2">
                <label>Date <span class="text-danger">*</span></label>
                <input type="date" class="form-control" v-model="manualTraining.training_date" />
              </div>
              <div class="col-md-4 mb-2">
                <label>Training Type</label>
                <AIVSelect v-model="manualTraining.training_type" :options="trainingTypeOptions" placeholder="Select type..." :clearable="true" />
              </div>
              <div class="col-md-4 mb-2">
                <label>Hours <span class="text-danger">*</span></label>
                <input type="number" class="form-control" v-model="manualTraining.hours_completed" min="0.25" step="0.25" placeholder="e.g. 2.5" />
              </div>
            </div>
            <div class="row">
              <div class="col-md-6 mb-2">
                <label>Title</label>
                <input type="text" class="form-control" v-model="manualTraining.training_title" placeholder="Training title" />
              </div>
              <div class="col-md-6 mb-2">
                <label>Supporting Document</label>
                <input type="file" class="form-control" ref="manualTrainingFileInput" @change="onManualTrainingFileChange" />
              </div>
            </div>
            <div class="row">
              <div class="col-12 mb-2">
                <label>Summary / Notes</label>
                <textarea class="form-control" v-model="manualTraining.summary" rows="2" placeholder="Brief description of training completed"></textarea>
              </div>
            </div>
            <div class="text-right">
              <button class="btn btn-primary" :disabled="!canSubmitManualTraining || submittingManualTraining" @click="submitManualTraining">
                <i v-if="submittingManualTraining" class="fa fa-spinner fa-spin"></i>
                {{ submittingManualTraining ? 'Saving...' : 'Save Training Record' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Training Records (Certificates & Hours Tracking) -->
        <div class="training-records-section">
          <h5>Training Records & Certificates</h5>

          <div class="training-hours-summary mb-3">
            <div class="row align-items-center">
              <div class="col-md-4">
                <label>Training Year</label>
                <AIVSelect
                  v-model="selectedTrainingYear"
                  :options="trainingYearOptions"
                  placeholder="Select Training Year"
                  @input="onTrainingYearChange"
                />
              </div>
              <div class="col-md-8">
                <div class="hours-total-card">
                  <div class="hours-total-label">Total Training Hours</div>
                  <div class="hours-total-value">
                    {{ trainingRecordsTotalHours }} hours
                    <span v-if="trainingYearStart && trainingYearEnd" class="training-year-range">
                      (Training year: {{ trainingYearStart }}&ndash;{{ trainingYearEnd }})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="loadingTrainingRecords" class="text-center p-3">
            <i class="fa fa-spinner fa-spin"></i> Loading training records...
          </div>

          <div v-else-if="trainingCertificateRecords.length === 0" class="text-center p-3">
            <p class="text-muted">No training records found for this training year.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Source</th>
                  <th>Training Type</th>
                  <th>Title</th>
                  <th>Hours</th>
                  <th>Summary</th>
                  <th>Certificate</th>
                  <th>Proof</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in trainingCertificateRecords" :key="record.id">
                  <td>{{ formatDateOnly(record.training_date) }}</td>
                  <td>
                    <span v-if="record.id_shifts" class="badge badge-secondary">Shift</span>
                    <span v-else class="badge badge-primary">Manual</span>
                  </td>
                  <td>{{ record.training_type || '-' }}</td>
                  <td>{{ record.training_title || '-' }}</td>
                  <td><span class="badge badge-info">{{ record.hours_completed }}h</span></td>
                  <td>
                    <span class="training-summary-preview" :title="record.summary">{{ truncateSummary(record.summary) }}</span>
                  </td>
                  <td>
                    <a v-if="record.certificate_path" :href="getCertificateUrl(record.id)" target="_blank" class="btn btn-sm btn-outline-primary" title="Download Certificate">
                      <i class="fa fa-file-pdf"></i> PDF
                    </a>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td>
                    <a v-if="record.external_proof_url" :href="getProofUrl(record.id)" target="_blank" class="btn btn-sm btn-outline-secondary" title="Download Proof">
                      <i class="fa fa-download"></i>
                    </a>
                    <label v-else class="btn btn-sm btn-outline-secondary mb-0" title="Upload Proof">
                      <i class="fa fa-upload"></i>
                      <input type="file" style="display:none" @change="uploadTrainingProof(record.id, $event)" />
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Compliance Tab -->
      <div v-if="activeTab === 'compliance'" class="admin-tab-pane">
        <div v-if="loadingCompliance" class="compliance-loading">
          <i class="fa fa-spinner fa-spin fa-2x"></i>
          <span>Loading compliance data...</span>
        </div>
        <div v-else-if="complianceProfile" class="compliance-content">
          <!-- Overall Status -->
          <div class="compliance-header mb-4">
            <h5>
              Compliance Status:
              <span :style="{ color: complianceStatusColor() }">
                <i :class="complianceStatusIcon()"></i>
                {{ complianceProfile.overallStatus.toUpperCase() }}
              </span>
            </h5>
            <p v-if="complianceProfile.hireDate" style="color: #999;">
              Hire Date: {{ complianceProfile.hireDate }}
            </p>
          </div>

          <!-- Training Hours Progress -->
          <div class="training-hours-section mb-4 p-3" style="background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h6>Annual Training Hours</h6>
            <el-progress
              :percentage="Math.min(100, Math.round((complianceProfile.trainingHours.totalHours / complianceProfile.trainingHours.required) * 100))"
              :color="complianceProfile.trainingHours.totalHours >= complianceProfile.trainingHours.required ? '#67c23a' : '#e6a23c'"
            />
            <p class="mt-1" style="font-size: 13px; color: #ccc;">
              {{ complianceProfile.trainingHours.totalHours }} / {{ complianceProfile.trainingHours.required }} hours
              <span v-if="complianceProfile.trainingHours.isFirstYear"> (First year - exempt)</span>
              <span v-else-if="complianceProfile.trainingHours.daysUntilAnniversary != null">
                &mdash; {{ complianceProfile.trainingHours.daysUntilAnniversary }} days until anniversary
              </span>
            </p>
            <el-button v-if="complianceProfile.trainingHours && !complianceProfile.trainingHours.isFirstYear" type="text" size="small" @click="showTrainingHistory = true" style="padding: 0;">
              <i class="fa fa-history"></i> View History
            </el-button>
          </div>

          <!-- Training History Modal -->
          <AIVDialog v-if="showTrainingHistory" title="Training History (Archive)" :visible.sync="showTrainingHistory" width="600px">
            <div class="p-3">
              <div v-if="loadingTrainingHistory" class="text-center p-3">
                <i class="fa fa-spinner fa-spin"></i> Loading...
              </div>
              <div v-else-if="trainingHistoryRecords.length === 0" class="text-center p-3">
                <p style="color: #999;">No archived training periods yet.</p>
              </div>
              <div v-else>
                <div v-for="record in trainingHistoryRecords" :key="record.id" class="p-2 mb-2" style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                  <div class="d-flex justify-content-between">
                    <strong>{{ record.period_start }} &mdash; {{ record.period_end }}</strong>
                    <span :style="{ color: record.met_requirement ? '#67c23a' : '#f56c6c' }">
                      {{ record.total_hours }} / {{ record.required_hours }} hrs
                      <i :class="record.met_requirement ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </AIVDialog>

          <!-- Document Breakdown by Tier -->
          <div v-for="item in complianceProfile.breakdown" :key="item.documentType" class="compliance-doc-row mb-2 p-2" style="display: flex; align-items: center; gap: 12px; border-bottom: 1px solid rgba(255,255,255,0.1);">
            <span :style="{ color: (item.tier === 4 && !item.isRequired && (item.status === 'missing' || item.status === 'rejected')) ? '#909399' : docStatusColor(item.status), fontSize: '18px' }">
              <i :class="(item.tier === 4 && !item.isRequired && item.status === 'missing') ? 'fa fa-minus-circle' : docStatusIcon(item.status)"></i>
            </span>
            <div style="flex: 1;">
              <strong>{{ item.documentType }}</strong>
              <span style="color: #999; font-size: 12px;"> ({{ item.tierLabel }})</span>
              <span v-if="item.tier === 4 && !item.isRequired" style="color: #909399; font-size: 11px; margin-left: 4px;">(only for ABI clients)</span>
              <div v-if="item.documentType === 'ABI/TBI Certification' && item.status === 'valid'" style="font-size: 12px; color: #67c23a;">
                Permanent (no expiration)
              </div>
              <div v-else-if="item.detail && item.detail.expirationDate" style="font-size: 12px; color: #aaa;">
                Expires: {{ item.detail.expirationDate }}
                <span v-if="item.daysUntilExpiration != null && item.daysUntilExpiration > 0"> ({{ item.daysUntilExpiration }} days)</span>
                <span v-else-if="item.daysUntilExpiration != null && item.daysUntilExpiration <= 0" style="color: #f44336;"> (EXPIRED)</span>
              </div>
              <div v-if="item.status === 'waivered' && item.detail" style="font-size: 12px; color: #FF9800;">
                Waivered until {{ item.detail.waiverExpirationDate }}
              </div>
              <div v-if="item.dueDate" style="font-size: 12px; color: #aaa;">
                Due by: {{ item.dueDate }}
                <span v-if="!item.isRequired" style="color: #67c23a;"> (not yet required)</span>
              </div>
              <div v-if="item.detail && item.detail.gracePeriodStart" style="font-size: 12px; color: #FF9800;">
                Grace period since {{ item.detail.gracePeriodStart }}
              </div>
            </div>
            <span class="compliance-status-badge" :style="{ background: (item.tier === 4 && !item.isRequired && item.status === 'missing') ? '#606060' : docStatusBadgeBg(item.status), color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', textTransform: 'uppercase' }">
              {{ (item.tier === 4 && !item.isRequired && item.status === 'missing') ? 'not needed' : item.status.replace('_', ' ') }}
            </span>
            <span v-if="item.detail && item.detail.id && !item.detail.filePath" style="font-size: 11px; color: #909399; margin-right: 8px;" title="Imported from ADP — no document file attached yet">Imported (date only)</span>
            <el-button v-if="item.detail && item.detail.id && item.detail.filePath" size="mini" icon="el-icon-view" @click="viewCompDoc(item.detail.id)" title="View Document">View</el-button>
            <el-button v-if="item.detail && item.detail.id && item.detail.filePath" size="mini" icon="el-icon-download" @click="downloadCompDoc(item.detail.id, item.documentType)" title="Download Document">Download</el-button>
            <el-button v-if="item.status === 'waivered' && item.detail && item.detail.id" size="mini" icon="el-icon-document" @click="viewCompWaiver(item.detail.id)" title="View Waiver">Waiver</el-button>
            <el-button v-if="item.detail && item.detail.id" size="mini" icon="el-icon-edit" @click="editCompDoc(item)" title="Edit">Edit</el-button>
            <el-button v-if="item.detail && item.detail.id" size="mini" type="danger" icon="el-icon-delete" @click="deleteCompDoc(item.detail.id, item.documentType)" title="Delete">Delete</el-button>
          </div>

          <!-- Edit Compliance Document Modal -->
          <AIVDialog v-if="showEditCompDocModal" title="Edit Compliance Document" :visible.sync="showEditCompDocModal" width="500px">
            <div class="p-3">
              <div class="mb-3">
                <label class="form-label">Document Type</label>
                <el-input :value="editingCompDoc.documentType" disabled />
              </div>
              <div class="mb-3">
                <label class="form-label">Expiration Date</label>
                <el-date-picker v-model="editingCompDoc.expirationDate" type="date" placeholder="MM/DD/YYYY" value-format="yyyy-MM-dd" format="MM/dd/yyyy" :editable="true" style="width: 100%;" />
              </div>
              <div class="mb-3">
                <label class="form-label">Status</label>
                <el-select v-model="editingCompDoc.status" style="width: 100%;">
                  <el-option label="Pending" value="Pending" />
                  <el-option label="Approved" value="Approved" />
                  <el-option label="Expired" value="Expired" />
                  <el-option label="Rejected" value="Rejected" />
                </el-select>
              </div>
            </div>
            <template #footer>
              <el-button @click="showEditCompDocModal = false">Cancel</el-button>
              <el-button type="primary" @click="saveEditCompDoc" :loading="savingCompDoc">Save</el-button>
            </template>
          </AIVDialog>

          <!-- Upload Document Form -->
          <div class="mt-4 p-3" style="background: rgba(255,255,255,0.05); border-radius: 8px;">
            <h6>Upload Compliance Document</h6>
            <div class="row">
              <div class="col-md-3 mb-2">
                <label class="form-label">Document Type</label>
                <el-select v-model="compUploadType" placeholder="Select type" style="width: 100%;">
                  <el-option label="Drivers License" value="Drivers License" />
                  <el-option label="Auto Insurance" value="Auto Insurance" />
                  <el-option label="Auto Registration" value="Auto Registration" />
                  <el-option label="CPR" value="CPR" />
                  <el-option label="SOAR" value="SOAR" />
                  <el-option label="ABI/TBI Certification" value="ABI/TBI Certification" />
                </el-select>
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label">Expiration Date</label>
                <el-date-picker v-model="compUploadExpiration" type="date" placeholder="MM/DD/YYYY" size="small" value-format="yyyy-MM-dd" format="MM/dd/yyyy" :editable="true" style="width: 100%;" />
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label">File</label>
                <div>
                  <input type="file" ref="compFileInput" accept="image/*,.pdf" style="display: none;" @change="compFileName = $event.target.files[0] ? $event.target.files[0].name : ''" />
                  <el-button size="small" icon="el-icon-folder-opened" @click="$refs.compFileInput.click()">
                    {{ compFileName || 'Choose File' }}
                  </el-button>
                </div>
              </div>
              <div class="col-md-3 mb-2">
                <label class="form-label">&nbsp;</label>
                <el-button type="primary" size="small" @click="uploadComplianceDoc" :loading="uploadingCompDoc" style="display: block;">
                  <i class="el-icon-upload2" style="margin-right: 4px;"></i> Upload
                </el-button>
              </div>
            </div>
          </div>

          <!-- Pending Approvals -->
          <div v-if="pendingCompDocs.length > 0" class="mt-4">
            <h6>Pending Approvals</h6>
            <div v-for="doc in pendingCompDocs" :key="doc.id" class="pending-doc-row p-2 mb-2" style="border: 1px solid rgba(255,193,7,0.3); border-radius: 6px;">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <strong>{{ doc.document_type }}</strong>
                  <span v-if="doc.ocr_expiration_date" style="color: #67c23a; font-size: 12px;"> Suggested date: {{ doc.ocr_expiration_date }}</span>
                </div>
                <div>
                  <el-button size="mini" icon="el-icon-view" @click="viewCompDoc(doc.id)" title="View Document">View</el-button>
                  <el-date-picker v-model="doc._approveDate" type="date" placeholder="MM/DD/YYYY" size="mini" value-format="yyyy-MM-dd" format="MM/dd/yyyy" :editable="true" class="mr-2" style="width: 160px;" />
                  <el-button size="mini" type="success" @click="approveCompDoc(doc)">Approve</el-button>
                  <el-button size="mini" type="danger" @click="rejectCompDoc(doc)">Reject</el-button>
                  <el-button size="mini" @click="grantCompWaiver(doc)">Waiver</el-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center p-4">
          <p>No compliance data available</p>
        </div>
      </div>

      <!-- Client Access Tab -->
      <div v-if="activeTab === 'client-access'" class="admin-tab-pane">
        <div class="client-access-section">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">Client Access Restrictions</h5>
            <button class="btn btn-sm btn-danger" @click="showBlockClientModal = true">
              <i class="fa fa-ban"></i> Block Client Access
            </button>
          </div>

          <p class="text-muted small mb-3">
            Admins have access to all clients by default. Use this section to explicitly block access to specific clients as needed.
          </p>

          <div v-if="loadingClientAccess" class="text-center p-3">
            <i class="fa fa-spinner fa-spin"></i> Loading...
          </div>

          <div v-else-if="clientAccessRecords.length === 0" class="text-center p-3">
            <p class="text-muted">No restrictions. This admin has access to all clients.</p>
          </div>

          <div v-else>
            <p class="text-muted small mb-2">
              <i class="fa fa-exclamation-triangle" style="color: #e6a23c;"></i>
              This admin is blocked from the following {{ clientAccessRecords.length }} client(s):
            </p>
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Payer Type</th>
                    <th>Blocked Since</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in clientAccessRecords" :key="record.id">
                    <td>
                      <span v-if="record.client">{{ record.client.first_name }} {{ record.client.last_name }}</span>
                      <span v-else class="text-muted">Unknown</span>
                    </td>
                    <td>{{ record.client ? record.client.payer_type : '-' }}</td>
                    <td>{{ formatDateShort(record.createdAt) }}</td>
                    <td>
                      <button class="btn btn-sm btn-outline-success" @click="unblockClientAccess(record)" title="Restore access">
                        <i class="fa fa-check"></i> Unblock
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Block Client Access Modal -->
        <AIVDialog v-if="showBlockClientModal" title="Block Client Access" :visible.sync="showBlockClientModal" width="500px">
          <div class="p-3">
            <div v-if="loadingAvailableClients" class="text-center p-3">
              <i class="fa fa-spinner fa-spin"></i> Loading clients...
            </div>
            <div v-else>
              <label>Select clients to block access to</label>
              <div class="available-clients-list" style="max-height: 400px; overflow-y: auto;">
                <div v-for="client in availableClientsForAccess" :key="client.id" class="form-check mb-1">
                  <input type="checkbox" class="form-check-input" :id="'block-client-' + client.id" :value="client.id" v-model="selectedClientsToBlock" />
                  <label class="form-check-label" :for="'block-client-' + client.id">
                    {{ client.first_name }} {{ client.last_name }}
                    <small class="text-muted">({{ client.payer_type || 'N/A' }})</small>
                  </label>
                </div>
                <p v-if="availableClientsForAccess.length === 0" class="text-muted">
                  All clients are already blocked for this admin.
                </p>
              </div>
            </div>
          </div>
          <template #footer>
            <button class="btn btn-default" @click="showBlockClientModal = false">Cancel</button>
            <button class="btn btn-danger" :disabled="selectedClientsToBlock.length === 0" @click="blockClientAccessBulk">
              Block Access ({{ selectedClientsToBlock.length }})
            </button>
          </template>
        </AIVDialog>
      </div>

      <!-- Calendar Tab -->
      <div v-if="activeTab === 'calendar'" class="admin-tab-pane">
        <div class="admin-schedule-container">
          <div class="schedule-content">
            <div class="modern-calendar-wrapper">
              <!-- Add New Shift Button -->
              <div class="calendar-header-actions">
                <el-button type="primary" size="small" @click="createShift" class="add-shift-button">
                  <i class="fa-solid fa-plus"></i> Add New Shift
                </el-button>
              </div>

              <!-- Modern Legend (shifts only, no work schedule) -->
              <div class="modern-legend-row">
                <div class="legend-groups">
                  <div class="legend-group">
                    <div class="legend-group-header">
                      <i class="fas fa-info-circle"></i>
                      <span>Shifts:</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-scheduled"></div>
                      <span>Scheduled</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-clocked-in"></div>
                      <span>Clocked In</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-clocked-out"></div>
                      <span>Clocked Out</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-approved"></div>
                      <span>Approved</span>
                    </div>
                    <div class="legend-item">
                      <div class="legend-color status-missed"></div>
                      <span>Missed</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Month Navigation Controls -->
              <div class="month-navigation">
                <button @click="goToPreviousMonth" class="nav-btn">
                  <i class="fas fa-chevron-left"></i>
                </button>
                <div class="current-month-display">
                  {{ currentMonthYear }}
                </div>
                <button @click="goToNextMonth" class="nav-btn">
                  <i class="fas fa-chevron-right"></i>
                </button>
              </div>

              <div class="calendar-view">
                <MonthView
                  v-if="!loadingShifts"
                  :currentMonth="currentMonth"
                  :daysInMonth="daysInMonth"
                  :dayNames="dayNames"
                  :eventsForDate="shiftsForDate"
                  :isToday="isToday"
                  :workSchedule="{}"
                  :getScheduledHours="() => null"
                  :approvedTimeOff="[]"
                  :showWeeklySummary="false"
                  pageType="caregiver"
                  @show-details="viewShiftDetails"
                />
                <div v-else class="text-center p-4">
                  <i class="fa fa-spinner fa-spin"></i> Loading schedule...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Shift Modal -->
    <ShiftModal
      :visible.sync="showShiftModal"
      :shift-being-edited="shiftBeingEdited"
      :available-clients="availableClients"
      :available-caregivers="[{ id: Number(adminId), name: adminName }]"
      :allow-delete="true"
      @cancel="closeShiftModal"
      @save="saveShift"
      @delete="handleEditModalDelete"
    />

    <!-- Shift Action Modal -->
    <ShiftActionModal
      :visible.sync="showShiftActionModal"
      :shift="selectedShiftForActions"
      :view-only="isViewOnlyMode"
      hideField="caregiver"
      @edit-shift="editShift"
      @edit-shift-notes="handleEditShiftNotes"
      @delete-shift="handleShiftDeletion"
      @clock-in="handleClockIn"
      @clock-out="handleClockOut"
      @admin-clock-out="handleAdminClockOut"
      @edit-clock-in-time="handleEditClockInTime"
      @edit-clock-out-time="handleEditClockOutTime"
      @approve-shift="handleApproveShift"
      @undo-approval="handleUndoApproval"
      @approve-missed-shift="handleApproveMissedShift"
      @cancel-shift="handleCancelShift"
      @open-tasks="handleOpenGroupTasks"
    />

    <!-- Delete Recurring Shift Dialog -->
    <DeleteRecurringShiftDialog
      :visible.sync="deleteRecurringDialog.visible"
      @this-only="deleteThisShiftOnly"
      @this-and-future="deleteThisAndFutureShifts"
      @all-shifts="deleteAllShifts"
    />

    <GroupShiftTasksModal
      :visible.sync="showGroupTasksModal"
      :shift="groupTasksShift"
      :user-id="adminId"
      @refresh="loadAdminShifts"
    />

    <!-- Clock In Modal -->
    <ClockInModal
      :visible.sync="showClockInModal"
      :shift="shiftToClockIn"
      :user-id="adminId"
      @clock-in-success="handleClockInSuccess"
    />

    <!-- Clock Out Modal -->
    <ClockOutModal
      :visible.sync="showClockOutModal"
      :shift="shiftToClockOut"
      :user-id="adminId"
      @clock-out-success="handleClockOutSuccess"
    />

    <AdminClockOutModal
      :visible.sync="showAdminClockOutModal"
      :shift="shiftToAdminClockOut"
      @clocked-out="loadAdminShifts"
    />

    <!-- Edit Clock In Time Modal -->
    <EditClockInTimeModal
      :visible.sync="showEditClockInTimeModal"
      :shift="shiftToEditClockInTime"
      @clock-in-time-updated="loadAdminShifts"
    />

    <!-- Edit Clock Out Time Modal -->
    <EditClockOutTimeModal
      :visible.sync="showEditClockOutTimeModal"
      :shift="shiftToEditClockOutTime"
      @clock-out-time-updated="loadAdminShifts"
    />

    <!-- Edit Shift Notes Modal -->
    <EditShiftNotesModal
      :visible.sync="showEditShiftNotesModal"
      :shift="shiftToEditNotes"
      @updated="loadAdminShifts"
    />

    <!-- Approve Shift Modal -->
    <ApproveShiftModal
      :visible.sync="showApproveShiftModal"
      :shift="shiftToApprove"
      @shift-approved="loadAdminShifts"
    />

    <!-- Approve Missed Shift Modal -->
    <ApproveMissedShift
      :visible.sync="showApproveMissedShiftModal"
      :shift="shiftToApproveMissed"
      @shift-approved="loadAdminShifts"
    />
  </div>
</template>

<script>
import { adminUsersService } from "@/serviceHandlers/adminUsersService";
import { officesService } from "@/serviceHandlers/officesService";
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { caregiverTrainingService } from "@/serviceHandlers/caregiverTrainingService";
import { trainingRecordsService } from "@/serviceHandlers/trainingRecordsService";
import { userClientAccessService } from "@/serviceHandlers/userClientAccessService";
import { documentsService } from "@/serviceHandlers/documentsService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { caregiverComplianceService } from "@/serviceHandlers/caregiverComplianceService";
import MonthView from "@/pages/Calendar/MonthView.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import AIVRow from "@/components/AIVRow.vue";
import { useLoginStore } from "@/stores/loginStore";
import { isDSPDGroupShift, getCaregiverShiftStatus } from "@/utils/shiftStatus";
import ShiftModal from "@/components/ShiftModal.vue";
import ShiftActionModal from "@/components/ShiftActionModal.vue";
import DeleteRecurringShiftDialog from "@/components/DeleteRecurringShiftDialog.vue";
import ClockInModal from "@/components/ClockInModal.vue";
import ClockOutModal from "@/components/ClockOutModal.vue";
import AdminClockOutModal from "@/components/AdminClockOutModal.vue";
import EditClockInTimeModal from "@/components/EditClockInTimeModal.vue";
import EditClockOutTimeModal from "@/components/EditClockOutTimeModal.vue";
import EditShiftNotesModal from "@/components/EditShiftNotesModal.vue";
import ApproveShiftModal from "@/components/ApproveShiftModal.vue";
import ApproveMissedShift from "@/components/ApproveMissedShift.vue";
import GroupShiftTasksModal from "@/components/GroupShiftTasksModal.vue";
import { parseDateOnly, localDateString } from "@/utils/dateOnly";

export default {
  name: "AdminDetails",
  components: {
    MonthView,
    AIVSelect,
    AIVRow,
    ShiftModal,
    ShiftActionModal,
    DeleteRecurringShiftDialog,
    ClockInModal,
    ClockOutModal,
    AdminClockOutModal,
    EditClockInTimeModal,
    EditClockOutTimeModal,
    EditShiftNotesModal,
    ApproveShiftModal,
    ApproveMissedShift,
    GroupShiftTasksModal,
  },
  data() {
    return {
      activeTab: "details",
      adminId: this.$route.params.id || null,
      adminName: this.$route.query.name || "Admin",
      // Calendar
      adminShifts: [],
      loadingShifts: false,
      currentMonth: new Date(),
      daysInMonth: [],
      dayNames: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      // Shift modals
      showShiftModal: false,
      shiftBeingEdited: null,
      showShiftActionModal: false,
      deleteRecurringDialog: { visible: false, shift: null },
      selectedShift: null,
      selectedShiftForActions: null,
      isViewOnlyMode: false,
      showClockInModal: false,
      shiftToClockIn: null,
      showClockOutModal: false,
      showAdminClockOutModal: false,
      shiftToAdminClockOut: null,
      shiftToClockOut: null,
      showEditClockInTimeModal: false,
      shiftToEditClockInTime: null,
      showEditClockOutTimeModal: false,
      shiftToEditClockOutTime: null,
      showEditShiftNotesModal: false,
      shiftToEditNotes: null,
      showApproveShiftModal: false,
      shiftToApprove: null,
      showApproveMissedShiftModal: false,
      shiftToApproveMissed: null,
      showGroupTasksModal: false,
      groupTasksShift: null,
      availableClients: [],
      // Details tab
      admin: null,
      loadingAdmin: false,
      officeOptions: [],
      businessUnitOptions: [
        { id: "MNG - Management", name: "MNG - Management" },
        { id: "VRS - Voc Rehab", name: "VRS - Voc Rehab" },
        { id: "CBS - Community Based Services", name: "CBS - Community Based Services" },
        { id: "HBS - Home Based Services", name: "HBS - Home Based Services" },
        { id: "HH - Host Home Services", name: "HH - Host Home Services" },
      ],
      // Training tab
      trainingRecords: [],
      loadingTraining: false,
      trainingDocuments: [],
      loadingDocuments: false,
      uploadingDocument: false,
      newDocument: { displayName: "", type: "file", url: "", file: null },
      documentTypeOptions: [
        { id: "file", name: "Upload File" },
        { id: "link", name: "Add Link" },
      ],
      trainingCertificateRecords: [],
      trainingRecordsTotalHours: 0,
      trainingYearStart: "",
      trainingYearEnd: "",
      selectedTrainingYear: null,
      loadingTrainingRecords: false,
      showManualTrainingForm: false,
      submittingManualTraining: false,
      manualTraining: {
        training_date: "",
        training_type: "",
        training_title: "",
        hours_completed: "",
        summary: "",
      },
      manualTrainingFile: null,
      trainingTypeOptions: [
        { id: "In-Service", name: "In-Service" },
        { id: "Online Course", name: "Online Course" },
        { id: "Conference", name: "Conference" },
        { id: "Workshop", name: "Workshop" },
        { id: "Orientation", name: "Orientation" },
        { id: "CPR/First Aid", name: "CPR/First Aid" },
        { id: "Medication Management", name: "Medication Management" },
        { id: "Safety Training", name: "Safety Training" },
        { id: "Client-Specific", name: "Client-Specific" },
        { id: "Other", name: "Other" },
      ],
      // Compliance tab
      complianceProfile: null,
      loadingCompliance: false,
      pendingCompDocs: [],
      compUploadType: "",
      compUploadExpiration: "",
      compFileName: "",
      uploadingCompDoc: false,
      showEditCompDocModal: false,
      editingCompDoc: { id: null, documentType: "", expirationDate: "", status: "" },
      savingCompDoc: false,
      selfUploadingDocType: null,
      showTrainingHistory: false,
      loadingTrainingHistory: false,
      trainingHistoryRecords: [],
      // Client access tab (deny model — records represent blocked clients)
      clientAccessRecords: [],
      loadingClientAccess: false,
      showBlockClientModal: false,
      availableClientsForAccess: [],
      loadingAvailableClients: false,
      selectedClientsToBlock: [],
    };
  },
  computed: {
    userId() {
      const store = useLoginStore();
      return store.user?.id;
    },
    canUpload() {
      if (!this.newDocument.displayName) return false;
      if (this.newDocument.type === "file") return !!this.newDocument.file;
      if (this.newDocument.type === "link") return !!this.newDocument.url;
      return false;
    },
    canSubmitManualTraining() {
      return this.manualTraining.training_date && parseFloat(this.manualTraining.hours_completed) > 0;
    },
    trainingYearOptions() {
      if (!this.admin || !this.admin.hire_date) return [];
      // hire_date is DATE-only — parse locally, and keep the window ids in
      // local parts too (toISOString would shift both by a day).
      const hireDate = parseDateOnly(this.admin.hire_date);
      const today = new Date();
      const options = [];
      const fmt = (d) =>
        `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
      const toISO = (d) => localDateString(d);
      let start = new Date(hireDate);
      while (start <= today) {
        const end = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate());
        options.push({
          id: `${toISO(start)}|${toISO(end)}`,
          name: `${fmt(start)}\u2013${fmt(end)}`,
        });
        start = end;
      }
      options.reverse();
      return options;
    },
    currentMonthYear() {
      const month = this.currentMonth.toLocaleString("default", { month: "long" });
      const year = this.currentMonth.getFullYear();
      return `${month} ${year}`;
    },
  },
  watch: {
    showTrainingHistory(val) {
      if (val) {
        this.loadTrainingHistory();
      }
    },
    showBlockClientModal(val) {
      if (val) {
        this.loadingAvailableClients = true;
        this.selectedClientsToBlock = [];
        userClientAccessService.availableClients(this.adminId)
          .then((clients) => { this.availableClientsForAccess = clients; })
          .catch((err) => { this.$notifyError("Error loading clients: " + err); })
          .finally(() => { this.loadingAvailableClients = false; });
      }
    },
  },
  async mounted() {
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab;
    }

    this.updateDaysInMonth();

    // Load admin details and offices in parallel
    await Promise.all([
      this.loadAdminDetails(),
      this.getOffices(),
    ]);

    // Load tab-specific data if navigated directly via URL
    if (this.activeTab === "calendar") {
      await this.loadAdminShifts();
    } else if (this.activeTab === "compliance") {
      this.loadComplianceProfile();
    } else if (this.activeTab === "client-access") {
      this.loadClientAccess();
    } else if (this.activeTab === "training") {
      this.loadTrainingTabData();
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: "Admin" });
    },

    // --- Details ---
    async loadAdminDetails() {
      this.loadingAdmin = true;
      try {
        const response = await adminUsersService.getById(this.adminId);
        this.admin = response;

        if (this.admin.first_name && this.admin.last_name) {
          this.adminName = `${this.admin.first_name} ${this.admin.last_name}`;
        }

        if (!this.admin.address) {
          this.admin.address = { address1: "", address2: "", city: "", state: "", zip: "" };
        }
      } catch (error) {
        this.$notifyError("Error loading admin details: " + error);
      } finally {
        this.loadingAdmin = false;
      }
    },

    async saveAdminDetails() {
      try {
        this.$spinner.show();
        await adminUsersService.update(this.adminId, this.admin);
        this.$notifySuccess("Admin details updated successfully");
        this.adminName = `${this.admin.first_name} ${this.admin.last_name}`;
      } catch (error) {
        this.$notifyError(
          error.response?.data?.message || error.response?.data?.error || "Error saving admin details"
        );
      } finally {
        this.$spinner.hide();
      }
    },

    async getOffices() {
      try {
        const offices = await officesService.list();
        this.officeOptions = (offices || []).map((o) => ({ id: o.id, name: o.name }));
      } catch (error) {
        // Error loading offices
      }
    },

    // --- Compliance ---
    async loadComplianceProfile() {
      if (!this.adminId) return;
      this.loadingCompliance = true;
      try {
        this.complianceProfile = await caregiverComplianceService.getComplianceProfile(this.adminId);
        this.pendingCompDocs = (this.complianceProfile.allDocuments || [])
          .filter((d) => d.status === "Pending")
          .map((d) => ({ ...d, _approveDate: d.expiration_date || "" }));
      } catch (e) {
      } finally {
        this.loadingCompliance = false;
      }
    },

    async uploadComplianceDoc() {
      if (!this.compUploadType) {
        this.$notifyError("Please select a document type");
        return;
      }
      const fileInput = this.$refs.compFileInput;
      if (!fileInput || !fileInput.files || !fileInput.files[0]) {
        this.$notifyError("Please select a file");
        return;
      }
      this.uploadingCompDoc = true;
      try {
        const formData = new FormData();
        formData.append("file", fileInput.files[0]);
        formData.append("document_type", this.compUploadType);
        formData.append("tier", this.getDocTier(this.compUploadType));
        if (this.compUploadExpiration) {
          formData.append("expiration_date", this.compUploadExpiration);
        }
        await caregiverComplianceService.upload(this.adminId, formData);
        this.$notifySuccess("Document uploaded. Set the expiration date and approve it below.");
        fileInput.value = "";
        this.compUploadType = "";
        this.compUploadExpiration = "";
        this.compFileName = "";
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError("Upload failed: " + (e.response?.data?.error || e.message));
      } finally {
        this.uploadingCompDoc = false;
      }
    },

    getDocTier(docType) {
      const tierMap = {
        "Drivers License": 1, "Auto Insurance": 1, "Auto Registration": 1,
        "CPR": 2, "SOAR": 3, "ABI/TBI Certification": 4,
      };
      return tierMap[docType] || 1;
    },

    async approveCompDoc(doc) {
      if (!doc._approveDate) {
        this.$notifyError("Please set the expiration date before approving");
        return;
      }
      try {
        await caregiverComplianceService.approve(doc.id, doc._approveDate);
        this.$notifySuccess("Document approved");
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError("Approval failed: " + (e.response?.data?.error || e.message));
      }
    },

    async rejectCompDoc(doc) {
      try {
        await caregiverComplianceService.reject(doc.id, "Rejected by admin");
        this.$notifySuccess("Document rejected");
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError("Rejection failed: " + (e.response?.data?.error || e.message));
      }
    },

    async grantCompWaiver(doc) {
      const waiverDate = doc._approveDate;
      if (!waiverDate) {
        this.$notifyError("Please set a waiver expiration date");
        return;
      }
      try {
        const formData = new FormData();
        formData.append("waiver_expiration_date", waiverDate);
        await caregiverComplianceService.grantWaiver(doc.id, formData);
        this.$notifySuccess("Waiver granted");
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError("Waiver failed: " + (e.response?.data?.error || e.message));
      }
    },

    async viewCompDoc(docId) {
      try {
        const blob = await caregiverComplianceService.viewDocument(docId);
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } catch (e) {
        this.$notifyError("Could not open document: " + (e.response?.data?.error || e.message));
      }
    },

    async downloadCompDoc(docId, documentType) {
      try {
        const blob = await caregiverComplianceService.downloadDocument(docId);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = (documentType || "document").replace(/\s+/g, "_") + "_" + docId;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$notifyError("Download failed: " + (e.response?.data?.error || e.message));
      }
    },

    async viewCompWaiver(docId) {
      try {
        const blob = await caregiverComplianceService.viewWaiver(docId);
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } catch (e) {
        this.$notifyError("Could not open waiver: " + (e.response?.data?.error || e.message));
      }
    },

    editCompDoc(item) {
      this.editingCompDoc = {
        id: item.detail.id,
        documentType: item.documentType,
        expirationDate: item.detail.expirationDate || "",
        status: item.detail.status || "Pending",
      };
      this.showEditCompDocModal = true;
    },

    async saveEditCompDoc() {
      this.savingCompDoc = true;
      try {
        await caregiverComplianceService.update(this.editingCompDoc.id, {
          expiration_date: this.editingCompDoc.expirationDate || null,
          status: this.editingCompDoc.status,
        });
        this.$notifySuccess("Document updated");
        this.showEditCompDocModal = false;
        await this.loadComplianceProfile();
      } catch (e) {
        this.$notifyError("Update failed: " + (e.response?.data?.error || e.message));
      } finally {
        this.savingCompDoc = false;
      }
    },

    async deleteCompDoc(docId, documentType) {
      try {
        await this.$confirm(`Delete ${documentType} document? This cannot be undone.`, "Confirm Delete", {
          confirmButtonText: "Delete",
          cancelButtonText: "Cancel",
          type: "warning",
        });
        await caregiverComplianceService.destroy(docId);
        this.$notifySuccess("Document deleted");
        await this.loadComplianceProfile();
      } catch (e) {
        if (e !== "cancel") {
          this.$notifyError("Delete failed: " + (e.response?.data?.error || e.message));
        }
      }
    },

    async loadTrainingHistory() {
      this.loadingTrainingHistory = true;
      try {
        this.trainingHistoryRecords = await caregiverComplianceService.getTrainingHistory(this.adminId);
      } catch (e) {
        this.trainingHistoryRecords = [];
      } finally {
        this.loadingTrainingHistory = false;
      }
    },

    complianceStatusColor() {
      if (!this.complianceProfile) return "#999";
      const s = this.complianceProfile.overallStatus;
      return s === "green" ? "#67c23a" : s === "yellow" ? "#e6a23c" : "#f44336";
    },

    complianceStatusIcon() {
      if (!this.complianceProfile) return "fa-solid fa-circle";
      const s = this.complianceProfile.overallStatus;
      return s === "green" ? "fa-solid fa-check-circle" : s === "yellow" ? "fa-solid fa-exclamation-circle" : "fa-solid fa-times-circle";
    },

    docStatusColor(status) {
      const map = { valid: "#67c23a", expiring_soon: "#e6a23c", expired: "#f44336", missing: "#909399", pending: "#409eff", waivered: "#FF9800", rejected: "#f44336" };
      return map[status] || "#999";
    },

    docStatusIcon(status) {
      const map = { valid: "fa-solid fa-check-circle", expiring_soon: "fa-solid fa-clock", expired: "fa-solid fa-times-circle", missing: "fa-solid fa-minus-circle", pending: "fa-solid fa-hourglass-half", waivered: "fa-solid fa-shield-halved", rejected: "fa-solid fa-ban" };
      return map[status] || "fa-solid fa-circle";
    },

    docStatusBadgeBg(status) {
      const map = { valid: "#67c23a", expiring_soon: "#e6a23c", expired: "#f44336", missing: "#909399", pending: "#409eff", waivered: "#FF9800", rejected: "#f44336" };
      return map[status] || "#999";
    },

    // --- Training ---
    async loadTrainingTabData() {
      if (this.trainingRecords && this.trainingRecords.length > 0) return;
      await Promise.all([
        this.loadTrainingRecords(),
        this.loadTrainingDocuments(),
        this.loadTrainingCertificateRecords(),
      ]);
    },

    async loadTrainingRecords() {
      this.loadingTraining = true;
      try {
        this.trainingRecords = await caregiverTrainingService.getByCaregiver(this.adminId);
      } catch (error) {
        this.$notifyError("Error loading training records: " + error);
      } finally {
        this.loadingTraining = false;
      }
    },

    async approveTraining(record) {
      try {
        this.$spinner.show();
        await caregiverTrainingService.review(record.id, "approved");
        this.$notifySuccess("Training approved successfully");
        await this.loadTrainingRecords();
      } catch (error) {
        this.$notifyError("Error approving training: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    async rejectTraining(record) {
      try {
        this.$spinner.show();
        await caregiverTrainingService.review(record.id, "rejected");
        this.$notifySuccess("Training rejected");
        await this.loadTrainingRecords();
      } catch (error) {
        this.$notifyError("Error rejecting training: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    async loadTrainingDocuments() {
      this.loadingDocuments = true;
      try {
        this.trainingDocuments = await documentsService.getCaregiverDocuments(this.adminId);
      } catch (error) {
        this.$notifyError("Error loading training documents: " + error);
      } finally {
        this.loadingDocuments = false;
      }
    },

    onManualTrainingFileChange(event) {
      this.manualTrainingFile = event.target.files[0] || null;
    },

    async submitManualTraining() {
      if (!this.canSubmitManualTraining) return;
      this.submittingManualTraining = true;
      try {
        const record = await trainingRecordsService.create({
          id_users: this.adminId,
          training_date: this.manualTraining.training_date,
          training_type: this.manualTraining.training_type || null,
          training_title: this.manualTraining.training_title || null,
          hours_completed: parseFloat(this.manualTraining.hours_completed),
          summary: this.manualTraining.summary || null,
        });

        if (this.manualTrainingFile && record.id) {
          try {
            await trainingRecordsService.uploadProof(record.id, this.manualTrainingFile);
          } catch (uploadErr) {
            this.$notifyWarning("Training saved but document upload failed: " + uploadErr);
          }
        }

        this.$notifySuccess("Training record added successfully");
        this.manualTraining = { training_date: "", training_type: "", training_title: "", hours_completed: "", summary: "" };
        this.manualTrainingFile = null;
        if (this.$refs.manualTrainingFileInput) {
          this.$refs.manualTrainingFileInput.value = "";
        }
        this.showManualTrainingForm = false;
        await this.loadTrainingCertificateRecords();
      } catch (error) {
        this.$notifyError("Error saving training record: " + (error.message || error));
      } finally {
        this.submittingManualTraining = false;
      }
    },

    async loadTrainingCertificateRecords() {
      this.loadingTrainingRecords = true;
      try {
        let startDate, endDate;
        if (this.selectedTrainingYear) {
          [startDate, endDate] = this.selectedTrainingYear.split("|");
        }
        const response = await trainingRecordsService.getByCaregiver(this.adminId, startDate, endDate);
        this.trainingCertificateRecords = response.records || [];
        this.trainingRecordsTotalHours = response.totalHours || 0;
        this.trainingYearStart = response.trainingYearStart || "";
        this.trainingYearEnd = response.trainingYearEnd || "";

        if (!this.selectedTrainingYear && response.trainingYearStart && response.trainingYearEnd) {
          const start = response.trainingYearStart;
          const end = response.trainingYearEnd;
          const toISO = (mdy) => {
            const [m, d, y] = mdy.split("/");
            return `${y}-${m}-${d}`;
          };
          this.selectedTrainingYear = `${toISO(start)}|${toISO(end)}`;
        }
      } catch (error) {
        this.$notifyError("Error loading training certificate records: " + error);
      } finally {
        this.loadingTrainingRecords = false;
      }
    },

    onTrainingYearChange() {
      this.loadTrainingCertificateRecords();
    },

    truncateSummary(text) {
      if (!text) return "-";
      return text.length > 60 ? text.substring(0, 60) + "..." : text;
    },

    getCertificateUrl(recordId) {
      return `/api/trainingRecords/${recordId}/certificate`;
    },

    getProofUrl(recordId) {
      return `/api/trainingRecords/${recordId}/proof`;
    },

    async uploadTrainingProof(recordId, event) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        this.$spinner.show();
        await trainingRecordsService.uploadProof(recordId, file);
        this.$notifySuccess("External proof uploaded successfully");
        await this.loadTrainingCertificateRecords();
      } catch (error) {
        this.$notifyError("Error uploading proof: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    onTrainingFileChange(event) {
      const file = event.target.files[0] || null;
      this.newDocument.file = file;
      if (file && !this.newDocument.displayName) {
        this.newDocument.displayName = file.name;
      }
    },

    async uploadTrainingDocument() {
      if (!this.canUpload) return;
      this.uploadingDocument = true;
      try {
        if (this.newDocument.type === "file") {
          const formData = new FormData();
          formData.append("file", this.newDocument.file);
          formData.append("caregiverId", this.adminId);
          formData.append("displayName", this.newDocument.displayName);
          const result = await documentsService.uploadCaregiver(formData);
          if (result.document) {
            this.trainingDocuments.unshift(result.document);
          }
          this.$notifySuccess("Document uploaded successfully");
        } else {
          const result = await documentsService.addCaregiverLink({
            caregiverId: this.adminId,
            displayName: this.newDocument.displayName,
            url: this.newDocument.url,
          });
          if (result.document) {
            this.trainingDocuments.unshift(result.document);
          }
          this.$notifySuccess("Link added successfully");
        }
        this.newDocument = { displayName: "", type: "file", url: "", file: null };
        if (this.$refs.trainingFileInput) {
          this.$refs.trainingFileInput.value = "";
        }
      } catch (error) {
        this.$notifyError("Error: " + (error.message || error));
      } finally {
        this.uploadingDocument = false;
      }
    },

    viewTrainingDocument(doc) {
      if (doc.is_link) {
        let url = doc.link;
        if (!url.startsWith("http://") && !url.startsWith("https://")) {
          url = "https://" + url;
        }
        window.open(url, "_blank");
      } else {
        const apiBaseUrl = process.env.VUE_APP_API_URL || window.location.origin;
        const filename = doc.link.split("/").pop();
        const fileUrl = `${apiBaseUrl}/api/pdf/client-document/${encodeURIComponent(filename)}`;
        const token = localStorage.getItem("jwtToken");
        fetch(fileUrl, { headers: { Authorization: token } })
          .then((response) => response.blob())
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
          })
          .catch((err) => {
            this.$notifyError("Error opening document: " + err.message);
          });
      }
    },

    async deleteTrainingDocument(doc) {
      if (!confirm(`Are you sure you want to delete "${doc.display_name || "this document"}"?`)) {
        return;
      }
      try {
        await documentsService.deleteCaregiverDocument(doc.id);
        this.trainingDocuments = this.trainingDocuments.filter((d) => d.id !== doc.id);
        this.$notifySuccess("Document deleted successfully");
      } catch (error) {
        this.$notifyError("Error deleting document: " + (error.message || error));
      }
    },

    // --- Client Access ---
    async loadClientAccess() {
      this.loadingClientAccess = true;
      try {
        this.clientAccessRecords = await userClientAccessService.getByUser(this.adminId);
      } catch (error) {
        this.$notifyError("Error loading client access: " + error);
      } finally {
        this.loadingClientAccess = false;
      }
    },

    async unblockClientAccess(record) {
      if (!record.client) return;
      const name = `${record.client.first_name} ${record.client.last_name}`;
      if (!confirm(`Restore access to ${name}?`)) return;
      try {
        await userClientAccessService.undeny(this.adminId, record.client.id);
        this.$notifySuccess(`Access to ${name} restored`);
        this.clientAccessRecords = this.clientAccessRecords.filter((r) => r.id !== record.id);
      } catch (error) {
        this.$notifyError("Error restoring access: " + error);
      }
    },

    async blockClientAccessBulk() {
      if (this.selectedClientsToBlock.length === 0) return;
      try {
        await userClientAccessService.denyBulk(this.adminId, this.selectedClientsToBlock);
        this.$notifySuccess(`Access blocked for ${this.selectedClientsToBlock.length} client(s)`);
        this.showBlockClientModal = false;
        this.selectedClientsToBlock = [];
        await this.loadClientAccess();
      } catch (error) {
        this.$notifyError("Error blocking access: " + error);
      }
    },

    // --- Calendar ---
    async loadAdminShifts() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();

      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);

      const params = {
        startDate: startDate.toISOString().split("T")[0] + "T00:00:00.000Z",
        endDate: endDate.toISOString().split("T")[0] + "T23:59:59.999Z",
        userType: "Admin",
        userId: this.adminId,
        perPage: 1000,
      };

      const response = await shiftsService.getByRole(params);

      this.adminShifts = (response.shifts || []).map((shift) => {
        let status = "scheduled";

        const store = useLoginStore();
        const currentUserId = store.user?.id;
        if (shift.shift_type === "Group" && shift.caregivers && shift.caregivers.length > 0) {
          const isAssigned = shift.caregivers.some((c) => Number(c.id) === Number(currentUserId));
          if (isAssigned) {
            const clockTimes = shift.caregiverClockTimes || [];
            const myClockTime = clockTimes.find((ct) => Number(ct.id_users) === Number(currentUserId));
            const overallStatus = (shift.shift_status || shift.status || "").toLowerCase();
            const shiftDate = (shift.date || shift.scheduled_date || "").split("T")[0];
            const today = new Date();
            const todayStr = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");
            const dayPast = shiftDate && shiftDate < todayStr;

            if (!myClockTime || !myClockTime.clock_in) {
              if (overallStatus === "approved") status = "approved";
              else if (overallStatus === "missed") status = "missed";
              else if (dayPast) status = "missed";
              else status = "scheduled";
            } else if (myClockTime.clock_in && !myClockTime.clock_out) {
              status = dayPast ? "error" : "clocked-in";
            } else if (myClockTime.clock_out) {
              status = overallStatus === "approved" ? "approved" : "clocked-out";
            }
          } else if (isDSPDGroupShift(shift)) {
            const statuses = shift.caregivers.map((c) => getCaregiverShiftStatus(shift, c.id));
            if (statuses.some((s) => s === "error")) status = "error";
            else if (statuses.every((s) => s === "approved")) status = "approved";
            else if (statuses.every((s) => s === "missed")) status = "missed";
            else if (statuses.every((s) => s === "missed" || s === "error")) status = "error";
            else if (statuses.some((s) => s === "clocked-in")) status = "clocked-in";
            else if (statuses.some((s) => s === "clocked-out")) status = "clocked-out";
          }
        } else if (shift.shift_status === "Clocked Out") {
          status = "clocked-out";
        } else if (shift.shift_status === "Clocked In") {
          status = "clocked-in";
        } else if (shift.shift_status === "Approved") {
          status = "approved";
        } else if (shift.shift_status === "Missed") {
          status = "missed";
        } else if (shift.shift_status === "Scheduled") {
          status = "scheduled";
        } else if (shift.shift_status === "Canceled") {
          status = "canceled";
        }

        if (status === "scheduled" && !shift.shift_status) {
          const shiftDate = (shift.date || shift.scheduled_date || "").split("T")[0];
          if (shiftDate) {
            const today = new Date();
            const todayStr = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");
            if (shiftDate < todayStr) {
              if (!shift.clockIn && !shift.clock_in) {
                status = "missed";
              } else {
                status = "error";
              }
            }
          }
        }

        const clientName =
          shift.payer === "Non Service"
            ? "N/A"
            : shift.clients && shift.clients.length > 0
              ? shift.clients[0].name
              : "Unknown Client";

        return {
          id: shift.id,
          date: shift.date,
          startTime: shift.scheduled_start || shift.startTime,
          endTime: shift.scheduled_end || shift.endTime,
          status: status,
          clientName: clientName,
          caregivers: shift.caregivers || [],
          clients: shift.clients || [],
          payer: shift.payer || "DSPD",
          shiftType: shift.shift_type || "1-on-1",
          groupName: shift.group_name || null,
          originalData: shift,
        };
      });
    },

    updateDaysInMonth() {
      const year = this.currentMonth.getFullYear();
      const month = this.currentMonth.getMonth();
      const arr = [];
      const first = new Date(year, month, 1);
      const firstWeekday = first.getDay();

      for (let i = 0; i < firstWeekday; i++) {
        arr.push(new Date(year, month, i - firstWeekday + 1));
      }

      const d = new Date(year, month, 1);
      while (d.getMonth() === month) {
        arr.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }

      const total = arr.length;
      const trailing = (7 - (total % 7)) % 7;
      for (let i = 1; i <= trailing; i++) {
        arr.push(new Date(year, month + 1, i));
      }

      this.daysInMonth = arr;
    },

    async goToPreviousMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() - 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
      this.loadingShifts = true;
      try {
        await this.loadAdminShifts();
      } finally {
        this.loadingShifts = false;
      }
    },

    async goToNextMonth() {
      const nm = new Date(this.currentMonth);
      nm.setMonth(nm.getMonth() + 1);
      this.currentMonth = nm;
      this.updateDaysInMonth();
      this.loadingShifts = true;
      try {
        await this.loadAdminShifts();
      } finally {
        this.loadingShifts = false;
      }
    },

    shiftsForDate(dateObj) {
      const y = dateObj.getFullYear();
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;

      return this.adminShifts
        .filter((shift) => shift.date === dateStr)
        .map((shift) => {
          let statusClass = "status-scheduled";
          if (shift.status === "missed") statusClass = "status-missed";
          else if (shift.status === "approved") statusClass = "status-approved";
          else if (shift.status === "clocked-out") statusClass = "status-clocked-out";
          else if (shift.status === "clocked-in") statusClass = "status-clocked-in";
          else if (shift.status === "error") statusClass = "status-error";
          else if (shift.status === "canceled") statusClass = "status-canceled";

          const hasClockIn = shift.status === "clocked-in" || shift.status === "clocked-out" || shift.status === "approved";
          const hasClockOut = shift.status === "clocked-out" || shift.status === "approved";

          const convertUTCDateTimeToLocal = (utcDateTimeStr) => {
            if (!utcDateTimeStr) return null;
            const utcDate = new Date(utcDateTimeStr);
            if (isNaN(utcDate.getTime())) return null;
            return `${String(utcDate.getHours()).padStart(2, "0")}:${String(utcDate.getMinutes()).padStart(2, "0")}`;
          };

          const caregiverClockTime = shift.originalData?.caregiverClockTimes?.find(
            (ct) => String(ct.id_users) === String(this.adminId)
          );

          const clockInTime = shift.originalData?.clock_in || shift.originalData?.clockIn || caregiverClockTime?.clock_in;
          const clockOutTime = shift.originalData?.clock_out || shift.originalData?.clockOut || caregiverClockTime?.clock_out;

          const actualStartTime = clockInTime ? convertUTCDateTimeToLocal(clockInTime) : null;
          const actualEndTime = clockOutTime ? convertUTCDateTimeToLocal(clockOutTime) : null;

          const displayStartTime = hasClockIn && actualStartTime ? actualStartTime : shift.startTime;
          const displayEndTime = hasClockOut && actualEndTime ? actualEndTime : shift.endTime;

          let clientDisplay = shift.clientName || "Unassigned";
          let groupName = null;
          if (shift.clients && shift.clients.length > 1) {
            groupName = shift.groupName || "Multiple";
          }

          return {
            ...shift,
            formattedStartTime: this.formatTime(displayStartTime),
            formattedEndTime: this.formatTime(displayEndTime),
            formattedScheduledStartTime: this.formatTime(shift.originalData?.scheduled_start || shift.startTime),
            formattedScheduledEndTime: this.formatTime(shift.originalData?.scheduled_end || shift.endTime),
            scheduledStartTime: shift.startTime,
            scheduledEndTime: shift.endTime,
            actualStartTime,
            actualEndTime,
            clockInRaw: clockInTime || null,
            clockOutRaw: clockOutTime || null,
            client: groupName ? null : clientDisplay,
            caregiver: null,
            groupName: groupName || null,
            statusClass,
            payer: shift.payer || "DSPD",
            shiftType: shift.shiftType || "1-on-1",
            hasClockIn,
            hasClockOut,
          };
        });
    },

    isToday(dateObj) {
      const now = new Date();
      return dateObj.getFullYear() === now.getFullYear() && dateObj.getMonth() === now.getMonth() && dateObj.getDate() === now.getDate();
    },

    formatTime(timeString) {
      if (!timeString) return "";
      if (timeString.includes("T")) {
        const date = new Date(timeString);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "pm" : "am";
        let displayHours = hours % 12;
        displayHours = displayHours ? displayHours : 12;
        return `${displayHours}:${minutes.toString().padStart(2, "0")}${ampm}`;
      }
      const timeParts = timeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${hours}:${minutes}${ampm}`;
    },

    // --- Shift interactions ---
    async createShift() {
      await this.loadAllClients();
      this.shiftBeingEdited = {
        caregiverId: this.adminId ? Number(this.adminId) : null,
        payer: "VR",
      };
      this.showShiftModal = true;
    },

    viewShiftDetails(shift) {
      this.selectedShift = shift;
      this.selectedShiftForActions = shift;

      const isShiftApproved = shift.status === "approved";
      const isShiftMissed = shift.status === "missed";

      if (isShiftApproved || isShiftMissed) {
        this.isViewOnlyMode = false;
        this.showShiftActionModal = true;
        return;
      }

      const isShiftClockedIn = shift.status === "clocked-in";
      const isShiftClockedOut = shift.status === "clocked-out";
      const isShiftError = shift.status === "error";
      const isAssigned = (shift.originalData?.caregivers || shift.caregivers || []).some(
        (c) => Number(c.id) === Number(this.userId)
      );

      // Admin can always edit - but show action modal for clocked/error shifts
      if (!isShiftClockedIn && !isShiftClockedOut && !isShiftError && !isAssigned) {
        this.editShift(shift);
        return;
      }

      this.isViewOnlyMode = false;
      this.showShiftActionModal = true;
    },

    async loadAllClients() {
      try {
        const response = await clientsService.list({ perPage: 1000, slim: true });
        const clients = response.data || response || [];
        this.availableClients = clients.map((client) => ({
          id: client.id,
          name: client.name || (client.first_name && client.last_name ? `${client.first_name} ${client.last_name}`.trim() : client.client_name) || "Unknown Client",
        }));
      } catch (error) {
        this.availableClients = [];
      }
    },

    async editShift(shift) {
      await this.loadAllClients();
      const clientIds = shift.clients && shift.clients.length > 0 ? shift.clients.map((c) => c.id) : [];
      const shiftToEdit = {
        ...shift.originalData,
        id: shift.id,
        date: shift.date,
        scheduled_start: shift.originalData?.scheduled_start || shift.startTime,
        scheduled_end: shift.originalData?.scheduled_end || shift.endTime,
        shift_type: shift.shiftType || shift.originalData?.shift_type,
        payer: shift.payer || shift.originalData?.payer,
        shift_status: shift.originalData?.shift_status || shift.status,
        clientId: clientIds.length > 0 ? clientIds[0] : null,
        caregiverId: this.adminId,
        clients: shift.originalData?.clients || [],
        caregivers: shift.originalData?.caregivers || [],
      };

      const status = (shift.status || shift.originalData?.shift_status)?.toLowerCase();
      const isClockedOut = status === "clocked-out" || status === "clocked out" || status === "error";
      if (isClockedOut) {
        this.shiftToEditClockOutTime = {
          ...shiftToEdit,
          startTime: shiftToEdit.scheduled_start,
          endTime: shiftToEdit.scheduled_end,
          originalData: { ...shift.originalData, scheduled_start: shiftToEdit.scheduled_start, scheduled_end: shiftToEdit.scheduled_end },
        };
        this.showEditClockOutTimeModal = true;
        return;
      }

      this.shiftBeingEdited = shiftToEdit;
      this.showShiftModal = true;
    },

    handleEditShiftNotes(shift) {
      const originalData = shift.originalData || shift;
      this.shiftToEditNotes = {
        ...originalData,
        id: shift.id,
        date: shift.date,
        startTime: originalData.scheduled_start,
        endTime: originalData.scheduled_end,
        originalData: { ...originalData, scheduled_start: originalData.scheduled_start, scheduled_end: originalData.scheduled_end },
      };
      this.showEditShiftNotesModal = true;
    },

    closeShiftModal() {
      this.showShiftModal = false;
      this.shiftBeingEdited = null;
    },

    async saveShift({ shift, shifts, isEditing, isGroupShift, isDSPDGroupShift, exceptionShiftIds }) {
      try {
        this.$spinner.show();

        if (isDSPDGroupShift && shift && !isEditing) {
          await shiftsService.create(shift);
          const clientCount = shift.clientIds?.length || 1;
          this.$notifySuccess(`DSPD group shift created successfully for ${clientCount} client(s)!`);
          this.closeShiftModal();
          await this.loadAdminShifts();
          return;
        }

        if (isGroupShift && shifts && Array.isArray(shifts)) {
          for (const shiftData of shifts) {
            await shiftsService.create(shiftData);
          }
          this.$notifySuccess(`${shifts.length} group shifts created successfully!`);
          this.closeShiftModal();
          await this.loadAdminShifts();
          return;
        }

        let savedShift;
        if (isEditing && shift.id) {
          savedShift = await shiftsService.update(shift.id, shift);
          this.$notifySuccess("Shift updated successfully");
        } else {
          savedShift = await shiftsService.create(shift);
          this.$notifySuccess("Shift created successfully");
        }

        // If the scheduler chose to keep individually-changed occurrences, move
        // them onto the new series the edit created (a series edit splits the
        // old series and starts a new one; exceptions stay tied to the old one
        // until re-parented).
        if (exceptionShiftIds && exceptionShiftIds.length > 0 && savedShift) {
          try {
            const newRecurringShiftId =
              savedShift.newRecurringShiftId || savedShift.id;
            if (newRecurringShiftId) {
              await shiftsService.handleExceptionShifts({
                action: "keep",
                exceptionShiftIds,
                newRecurringShiftId,
              });
              this.$notifySuccess("Changed occurrences kept on the new series");
            }
          } catch (exceptionError) {
            this.$notifyError(
              "Shift saved, but the changed occurrences could not be moved to the new series",
            );
          }
        }

        this.closeShiftModal();
        await this.loadAdminShifts();
      } catch (error) {
        this.$notifyError("Error saving shift: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    async handleClockIn(shift) {
      if (!(await this.$clockNudge("in"))) return;
      this.shiftToClockIn = shift.originalData || shift;
      this.showClockInModal = true;
    },

    async handleClockInSuccess() {
      await this.loadAdminShifts();
      this.$root.$emit("shift-saved");
    },

    handleOpenGroupTasks(shift) {
      this.groupTasksShift = shift.originalData || shift;
      this.showGroupTasksModal = true;
    },

    async handleClockOut(shift) {
      if (!(await this.$clockNudge("out"))) return;
      this.shiftToClockOut = {
        ...shift.originalData,
        id: shift.id,
        date: shift.date,
        startTime: shift.startTime,
        endTime: shift.endTime,
        scheduled_start: shift.originalData?.scheduled_start || shift.startTime,
        scheduled_end: shift.originalData?.scheduled_end || shift.endTime,
        clients: shift.clients || shift.originalData?.clients || [],
        caregivers: shift.originalData?.caregivers || [],
        payer: shift.payer || shift.originalData?.payer,
        shift_type: shift.shiftType || shift.originalData?.shift_type,
      };
      this.showClockOutModal = true;
      this.showShiftActionModal = false;
    },

    async handleClockOutSuccess() {
      await this.loadAdminShifts();
    },

    handleAdminClockOut(shift) {
      this.shiftToAdminClockOut = shift;
      this.showAdminClockOutModal = true;
      this.showShiftActionModal = false;
    },

    handleEditClockInTime(shift) {
      this.shiftToEditClockInTime = shift;
      this.showEditClockInTimeModal = true;
    },

    handleEditClockOutTime(shift) {
      this.shiftToEditClockOutTime = shift;
      this.showEditClockOutTimeModal = true;
    },

    handleApproveShift(shift) {
      this.shiftToApprove = {
        ...shift.originalData,
        startTime: shift.startTime || shift.originalData?.scheduled_start,
        endTime: shift.endTime || shift.originalData?.scheduled_end,
        scheduledStartTime: shift.originalData?.scheduled_start || shift.startTime,
        scheduledEndTime: shift.originalData?.scheduled_end || shift.endTime,
        clockInTime: shift.originalData?.clockIn || shift.originalData?.clock_in,
        clockOutTime: shift.originalData?.clockOut || shift.originalData?.clock_out,
        client: shift.client || shift.clientName,
        caregiver: shift.caregiver || this.adminName,
        caregiverId: this.adminId,
        clientId: shift.clients && shift.clients.length > 0 ? shift.clients[0].id : null,
        payer: shift.payer || shift.originalData?.payer,
        shiftType: shift.shiftType || shift.originalData?.shift_type,
        originalData: {
          ...shift.originalData,
          scheduled_start: shift.originalData?.scheduled_start,
          scheduled_end: shift.originalData?.scheduled_end,
          clockIn: shift.originalData?.clockIn || shift.originalData?.clock_in,
          clock_in: shift.originalData?.clockIn || shift.originalData?.clock_in,
          clockOut: shift.originalData?.clockOut || shift.originalData?.clock_out,
          clock_out: shift.originalData?.clockOut || shift.originalData?.clock_out,
        },
      };
      this.showApproveShiftModal = true;
    },

    async handleUndoApproval(shift) {
      try {
        const confirmed = await this.$confirmAction(
          () => {},
          "Are you sure you want to undo this shift approval? This will restore the authorization hours."
        );
        if (confirmed) {
          this.$spinner.show();
          await shiftsService.undoShiftApproval({ shiftId: shift.id });
          this.$notifySuccess("Shift approval undone successfully! Authorization hours have been restored.");
          this.showShiftActionModal = false;
          await this.loadAdminShifts();
        }
      } catch (error) {
        this.$notifyError("Error undoing approval: " + error);
      } finally {
        this.$spinner.hide();
      }
    },

    handleApproveMissedShift(shift) {
      this.shiftToApproveMissed = {
        ...shift.originalData,
        scheduledStartTime: shift.originalData?.scheduled_start || shift.startTime,
        scheduledEndTime: shift.originalData?.scheduled_end || shift.endTime,
        client: shift.client || shift.clientName,
        caregiver: shift.caregiver || this.adminName,
        caregiverId: this.adminId,
        clientId: shift.clients && shift.clients.length > 0 ? shift.clients[0].id : null,
        payer: shift.payer || shift.originalData?.payer,
        shiftType: shift.shiftType || shift.originalData?.shift_type,
      };
      this.showApproveMissedShiftModal = true;
    },

    async handleCancelShift(shift) {
      const shiftId = shift.id || shift.originalData?.id;
      if (!shiftId) return;
      let reason;
      try {
        const result = await this.$prompt(
          "Please provide a reason for canceling this shift:",
          "Cancel Shift",
          {
            confirmButtonText: "Cancel Shift",
            cancelButtonText: "Go Back",
            inputType: "textarea",
            inputPlaceholder: "e.g., client canceled, schedule conflict, weather...",
            inputValidator: (val) => {
              if (!val || !val.trim()) return "A cancellation reason is required";
              return true;
            },
          }
        );
        reason = result.value;
      } catch {
        return;
      }
      try {
        await shiftsService.update(shiftId, { shift_status: "Canceled", adminChangeReason: reason.trim() });
        this.$notifySuccess("Shift canceled successfully");
        await this.loadAdminShifts();
      } catch (error) {
        this.$notifyError(error.response?.data?.message || "Error canceling shift");
      }
    },

    handleEditModalDelete(shift) {
      this.closeShiftModal();
      this.handleShiftDeletion(shift);
    },
    // Delete a shift, mirroring the Calendar page: recurring shifts get the
    // 3-option dialog, others a simple confirm.
    async handleShiftDeletion(shift) {
      const original = shift.originalData || shift;
      const isRecurring =
        original.recurring ||
        original.parent_recurring_shift_id ||
        shift.recurring ||
        shift.parent_recurring_shift_id;

      try {
        if (isRecurring) {
          this.deleteRecurringDialog = { visible: true, shift };
        } else {
          const confirmDelete = await this.$confirmAction(
            null,
            "Are you sure you want to delete this shift?",
          );
          if (!confirmDelete) return;
          await shiftsService.delete(shift.id);
          this.$notifySuccess("Shift deleted successfully!");
          this.showShiftActionModal = false;
          await this.loadAdminShifts();
        }
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },
    async deleteThisShiftOnly() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteWithDate(shift.id, shiftDate);
        this.$notifySuccess("Shift occurrence deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadAdminShifts();
      } catch (error) {
        this.$notifyError("Error deleting shift: " + error);
      }
    },
    async deleteThisAndFutureShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const original = shift.originalData || shift;
        const parentId =
          original.parent_recurring_shift_id ||
          shift.parent_recurring_shift_id ||
          shift.id;
        const shiftDate = shift.date ? shift.date.split("T")[0] : null;
        await shiftsService.deleteFromDate(parentId, shiftDate);
        this.$notifySuccess("This and all future occurrences deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadAdminShifts();
      } catch (error) {
        this.$notifyError("Error deleting shifts: " + error);
      }
    },
    async deleteAllShifts() {
      const shift = this.deleteRecurringDialog.shift;
      this.deleteRecurringDialog = { visible: false, shift: null };
      try {
        const original = shift.originalData || shift;
        const parentId =
          original.parent_recurring_shift_id ||
          shift.parent_recurring_shift_id ||
          shift.id;
        await shiftsService.deleteEntireSeries(parentId);
        this.$notifySuccess("Entire recurring series deleted successfully!");
        this.showShiftActionModal = false;
        await this.loadAdminShifts();
      } catch (error) {
        this.$notifyError("Error deleting series: " + error);
      }
    },

    // --- Formatting ---
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    },

    // For DATE-only columns (e.g. training_date "2026-07-22"): new Date()
    // parses those as UTC midnight, which renders as the PREVIOUS day (with a
    // bogus time) in any US timezone. Parse the parts as a plain local date.
    formatDateOnly(dateString) {
      if (!dateString) return "-";
      const m = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
      const date = m
        ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
        : new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },

    formatDateShort(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },
  },
};
</script>

<style scoped>
.compliance-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: #888;
}

.compliance-loading i {
  color: #6ba6cd;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  flex: 1;
}

.back-button {
  font-size: 14px;
  padding: 8px 16px;
  height: 40px;
}

.back-button i {
  margin-right: 6px;
}

.admin-name {
  margin: 0;
  flex: 0 0 auto;
}

.button-toggle-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  margin: 0;
  margin-top: -3px;
}

.toggle-button {
  border-radius: 0;
  min-width: 120px;
  margin: 0;
}

.toggle-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.toggle-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.toggle-button:not(:first-child) {
  border-left: none;
}

:deep(.toggle-button.el-button--default) {
  background-color: #606266;
  border-color: #606266;
  color: #fff;
}

:deep(.toggle-button.el-button--default:hover) {
  background-color: #737373;
  border-color: #737373;
  color: #fff;
}

:deep(.toggle-button.el-button--primary) {
  background-color: #409eff;
  border-color: #409eff;
  color: #fff;
}

.admin-tab-content {
  margin-top: 1rem;
}

.admin-tab-pane {
  padding: 0;
  color: #fff;
  min-height: 200px;
}

.admin-tab-pane > p {
  color: #fff;
  font-size: 1rem;
  margin: 0;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .header-left {
    width: 100%;
  }

  .button-toggle-row {
    width: 100%;
  }

  .toggle-button {
    flex: 1;
  }
}

.admin-schedule-container {
  width: 100%;
}

.modern-calendar-wrapper {
  width: 100%;
}

.calendar-header-actions {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}

.add-shift-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Modern Legend Row */
.modern-legend-row {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.legend-groups {
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
}

.legend-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.legend-group-header {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6ba6cd;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  flex-shrink: 0;
}

.legend-group-header i {
  font-size: 0.75rem;
  width: 12px;
  text-align: center;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  padding: 0.25rem 0;
  transition: color 0.2s ease;
}

.legend-item:hover {
  color: #fff;
}

.legend-color {
  width: 14px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.legend-item:hover .legend-color {
  transform: scale(1.1);
}

.legend-color.status-scheduled {
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.8), rgba(13, 110, 253, 0.6));
}

.legend-color.status-clocked-in {
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.9), rgba(255, 193, 7, 0.7));
}

.legend-color.status-clocked-out {
  background: linear-gradient(135deg, rgba(237, 137, 54, 0.7), rgba(237, 137, 54, 0.5));
}

.legend-color.status-approved {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.7), rgba(156, 39, 176, 0.5));
}

.legend-color.status-missed {
  background: linear-gradient(135deg, rgba(108, 117, 125, 0.8), rgba(108, 117, 125, 0.6));
}

/* Month Navigation */
.month-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin: 1rem 0;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 36px;
  width: 36px;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.nav-btn i {
  font-size: 12px;
}

.current-month-display {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  min-width: 200px;
  text-align: center;
  letter-spacing: 0.5px;
}

.calendar-view {
  margin-top: 1rem;
  width: 100%;
}

/* Calendar-specific styles */
:deep(.custom-calendar) { width: 100%; }
:deep(.calendar-grid) { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); }
:deep(.day-name) { font-weight: bold; text-align: center; border-right: 1px solid rgba(255, 255, 255, 0.2); border-bottom: 1px solid rgba(255, 255, 255, 0.2); padding: 8px; color: #fff; background: rgba(255, 255, 255, 0.05); }
:deep(.calendar-day) { position: relative; background: transparent; border-right: 1px solid rgba(255, 255, 255, 0.2); border-bottom: 1px solid rgba(255, 255, 255, 0.2); min-height: 80px; padding: 4px; cursor: pointer; }
:deep(.calendar-day:nth-child(7n)) { border-right: none; }
:deep(.calendar-day:nth-last-child(-n + 7)) { border-bottom: none; }
:deep(.day-name:nth-child(7n)) { border-right: none; }
:deep(.date-number) { font-size: 0.85rem; font-weight: bold; color: #fff; }
:deep(.today) { background-color: rgba(107, 166, 205, 0.2); border: 2px solid #6ba6cd; }
:deep(.other-month) { opacity: 0.4; }
:deep(.shift-item) { margin-top: 2px; font-size: 0.7rem; color: #fff; padding: 4px 6px; min-height: 24px; border-radius: 3px; cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; justify-content: space-between; align-items: center; gap: 4px; }
:deep(.shift-time) { font-weight: bold; flex-shrink: 0; }
:deep(.shift-client) { overflow: hidden; text-overflow: ellipsis; opacity: 0.9; }

/* Training styles */
.hours-total-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px 16px;
}

.hours-total-label {
  color: #aaa;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.hours-total-value {
  color: #6ba6cd;
  font-size: 1.4rem;
  font-weight: 700;
}

.training-year-range {
  font-size: 0.85rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
}

.training-summary-preview {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  cursor: default;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.document-icon {
  color: #6ba6cd;
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
}

.document-info {
  flex: 1;
}

.document-name {
  color: #6ba6cd;
  text-decoration: none;
}

.document-name:hover {
  text-decoration: underline;
}

.document-meta {
  font-size: 0.8rem;
  color: #999;
}
</style>
